import React from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetAllMeetingTemplatesQuery } from '../../../../store/ai/meetingTemplate'
import { useGetAllEmailTypesQuery } from '../../../../store/integrations/emailTypes'
import { useGetAllDocumentTypesQuery } from '../../../../store/ai/documentTypes'
import { useListTemplatesQuery } from '../../../../store/templateLibrary/templateLibrary'
import { type TemplateLookup, type TemplateScope } from '../../../../models/TemplateLibrary'
import { type SelectOption } from '../../../SelectDropdown/SelectDropdown'
import { InputParameterHierarchyType } from '../../../InputParameterHierarchy/model'
import { toOptions } from './referenceOptions'
import { EMAIL_TYPE_KIND_BY_IPH_TYPE } from './iphBindings'

export interface ReferenceOptionsSource {
  options: SelectOption[]
  loading: boolean
}

export const optionLabelOf = ({ options }: ReferenceOptionsSource, value: string, fallback: string): string =>
  options.find((option) => option.id === value)?.label ?? fallback

export const useMeetingTemplateOptions = (): ReferenceOptionsSource => {
  const accountId = useAppSelector(selectAccountId)
  const { data, isLoading } = useGetAllMeetingTemplatesQuery({ accountId, includeInactive: false, excludeDeprecated: true })
  return { options: toOptions(data?.data), loading: isLoading }
}

export const useEmailTypeOptions = (iphType: keyof typeof EMAIL_TYPE_KIND_BY_IPH_TYPE): ReferenceOptionsSource => {
  const accountId = useAppSelector(selectAccountId)
  const { data, isLoading } = useGetAllEmailTypesQuery({ accountId, includeInactive: false, kind: EMAIL_TYPE_KIND_BY_IPH_TYPE[iphType] })
  return { options: toOptions(data?.data), loading: isLoading }
}

export const useDocumentTypeOptions = (): ReferenceOptionsSource => {
  const accountId = useAppSelector(selectAccountId)
  const { data, isLoading } = useGetAllDocumentTypesQuery({ accountId, includeInactive: false })
  return { options: toOptions(data?.data), loading: isLoading }
}

const SCOPE_SUFFIX: Record<TemplateScope, string> = {
  system: 'System',
  account: 'Account',
  enterprise: 'Enterprise',
  user: 'Yours',
}

const buildDisplayLabel = (name: string, scope: TemplateScope): React.ReactNode => (
  <>
    {name} <span className='paragraph-small text-muted'>· {SCOPE_SUFFIX[scope]}</span>
  </>
)

const isVisible = (template: TemplateLookup, layer: 'account' | 'user', currentUserId: string | undefined): boolean => {
  if (template.scope !== 'user') {
    return true
  }
  if (layer === 'account') {
    return false
  }
  return template.createdBy === currentUserId
}

export const useTemplateLibraryOptions = (templateType: string, typeId: string | undefined, layer: 'account' | 'user'): ReferenceOptionsSource => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectAuthenticatedUser)?.id
  const defaultTypeId = templateType === InputParameterHierarchyType.AI_FEATURE_PRESET ? typeId : undefined
  const { data, isLoading } = useListTemplatesQuery({ accountId, type: templateType, typeId, defaultTypeId })

  const options = (data?.data ?? [])
    .filter((template) => isVisible(template, layer, currentUserId))
    .map((template) => ({ id: template.id, label: template.name, displayLabel: buildDisplayLabel(template.name, template.scope) }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return { options, loading: isLoading }
}
